import React, { ComponentType, useRef } from 'react';
import { withProductForm } from 'bnc-react-forms';

import { AcmEligibilityResponse } from 'components/Alerts/AccountEligibilityRulesAlert/types';
import { getEligibilityFieldError } from 'components/Alerts/AccountEligibilityRulesAlert/utils/accountEligibilityRules.utils';
import { ErrorType } from '../types';
import { EligibilityProductForm, EligibilityFieldProps, EligibilityFieldErrorProps } from './types';

export const getProductFormEligibilityError = (
  productForm: EligibilityProductForm,
  fieldName: string,
): ErrorType | undefined =>
  getEligibilityFieldError(
    productForm.values?.acmEligibilityResponses,
    productForm.values?.acmEligibilityFieldMappingsByRule ?? {},
    fieldName,
  );

export const withEligibilityFieldError = (component: any) =>
  withProductForm((productForm: EligibilityProductForm, props: EligibilityFieldProps) => ({
    eligibilityError: getProductFormEligibilityError(productForm, props.name),
    eligibilityResponses: productForm.values?.acmEligibilityResponses,
  }))(component);

export const withEligibilityFieldErrorDismissal = <TProps extends EligibilityFieldErrorProps>(
  Component: ComponentType<TProps>,
) => {
  const EligibilityFieldErrorDismissal = (props: TProps) => {
    const previousResponsesRef = useRef<AcmEligibilityResponse[] | undefined>();
    const dismissedRef = useRef(false);

    // Reset the dismissal whenever the BFF sends a new eligibility response (new array reference).
    if (previousResponsesRef.current !== props.eligibilityResponses) {
      previousResponsesRef.current = props.eligibilityResponses;
      dismissedRef.current = false;
    }

    const handleChange: NonNullable<typeof props.input.onChange> = (...args) => {
      dismissedRef.current = true;
      return props.input.onChange?.(...args);
    };

    // Some fields (e.g. ProductFormDateFields) commit user edits through an imperative `change(name, value)`
    // prop instead of `input.onChange`, bypassing the wrapped handler above. Wrap it too so a genuine user
    // edit still dismisses the eligibility error, regardless of which path the field uses to commit values.
    const { change } = props;
    const handleFieldChange = change
      ? (field: string, value: unknown) => {
          dismissedRef.current = true;
          return change(field, value);
        }
      : undefined;

    const eligibilityError = props.eligibilityError && !dismissedRef.current ? props.eligibilityError : undefined;

    return (
      <Component
        {...props}
        input={{ ...props.input, onChange: handleChange }}
        {...(handleFieldChange ? { change: handleFieldChange } : {})}
        eligibilityError={eligibilityError}
      />
    );
  };

  return EligibilityFieldErrorDismissal;
};
